<template>
	<div class='fi-list' v-if="hasForce || hasBlocks || hasComments || hasWarnings">
		<template v-if="hasForce || hasBlocks">
			<div class="fi-header">{{ env.msg('title.items.forceInfo') }}</div>
			<template v-if="fis.forceInfoTotal > 0">
				<TransitionGroup :name="changeTransition">
					<force-info v-for="f in forceInfos" :fi="f" :key="f.id" :reload="reload"></force-info>
				</TransitionGroup>
				<div v-if="needPager" class="fi-pager"><sym-icon name="page-prev-16-bold" class="fi-pager-icon" :class="{ 'fi-pager-disabled': fis.forceInfoPosition===0 }" @click.prevent="fis.forceInfoPrev()"></sym-icon><span class="fi-counter">{{ fis.forceInfoPosition + 1 }}-{{
						fis.forceInfoPosition + forceInfos.length
					}} / {{ fis.forceInfoTotal }}</span><sym-icon name="page-next-16-bold" class="fi-pager-icon" :class="{ 'fi-pager-disabled': (fis.forceInfoPosition + forceInfos.length) >= fis.forceInfoTotal }" @click.prevent="fis.forceInfoNext()"></sym-icon></div>
			</template>
			<object-block v-if="hasBlocks" :blocks="blockingParties"></object-block>
		</template>
		<template v-if="hasComments || hasWarnings">
			<div class="fi-header">{{ env.msg('comment.customerWarning') }}</div>
			<TransitionGroup v-if="hasComments" :name="changeTransition">
				<object-comment v-for="n in comments" :comment="n" :key="n.id"></object-comment>
			</TransitionGroup>
			<template v-if="hasWarnings">
				<warning-message v-for="w in warnings" :warn="w"></warning-message>
			</template>
		</template>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted} from "vue";
import {useForceInfoStore} from "@/store/ForceInfoStore.ts";
import {injectEnv} from "@fd/Environment.ts";
import {storeToRefs} from "pinia";
import SymIcon from "@fd/helpers/SymIcon.vue";
import ForceInfo from "@/processes/ForceInfo.vue";
import {Comment, Warning} from "@t/note/Comment.ts";
import ObjectComment from "@/processes/ObjectComment.vue";
import WarningMessage from "@/processes/WarningMessage.vue";
import ObjectBlock from "@/processes/ObjectBlock.vue";

const props = defineProps<{
	responsibles?: number[],
	srcType?:number;
	srcId?:number|string;

	/** Reload current page on force info completion (use on JSP pages) */
	reload?: boolean,
	collectingFeedback?: boolean,
	/** Array of users login/IDs blocking row object */
	blocks?:(string|number)[],
	comments?: Comment[],
	warnings?: (Warning|string)[],
	notesOnly?: boolean
}>();
const fis = useForceInfoStore();
const { forceInfos, blockers, feedbackForceInSize } = storeToRefs(fis);
const env = injectEnv();
const changeTransition = ref<string>("none");
const needPager = computed<boolean>(() => fis.forceInfoTotal > forceInfos.value.length);
const hasComments = computed<boolean>(() => Array.isArray(props.comments) && props.comments.length > 0);
const hasWarnings = computed<boolean>(() => Array.isArray(props.warnings) && props.warnings.length > 0);
const hasBlocks = computed<boolean>(() => Array.isArray(props.blocks) && props.blocks.length > 0 || blockers.value.length > 0);
const hasForce = computed<boolean>(() => !props.notesOnly && (fis.forceInfoTotal > 0 || hasBlocks.value));

const blockingParties = computed<(string|number)[]>(() => {
	if (Array.isArray(props.blocks) && props.blocks.length > 0) return props.blocks;
	else return blockers.value;
});

onMounted(() => {
	if (!props.notesOnly) {
		fis.setForceInfoContext(props.srcType ?? 0, props.srcId ?? 0, props.responsibles ?? []);
	}
});
</script>

<style>

/* Container for installing fi-list */
.fi-container:empty {
	display: none;
}

.fi-list {
	background-color: var(--bg);
	padding: var(--view-header-v-padding) 0;
	margin-bottom: 16px;
	color: var(--fg);
	line-height: var(--icn-size);
	border-radius: var(--panel-radius);
	overflow: hidden;
	box-shadow: var(--panel-shadow);
	container-type: inline-size;
}
.fi-seen {
}
.fi-header {
	padding-left: var(--view-header-v-padding);
	padding-right: var(--view-header-v-padding);
	margin-bottom: var(--gap);
	font-size: var(--form-header-font-size);
	font-weight: bold;

	&:not(:first-child) {
		padding-top: var(--view-header-v-padding);
	}
}

.fi-pager {
	margin-top: 8px;
	padding-left: calc(var(--view-header-v-padding) + 30px);
	padding-right: var(--view-header-v-padding);
	text-align: left;
	user-select: none;
	-webkit-user-select: none;

	&:not(:last-child) {
		margin-bottom: 8px;
	}
}

.fi-date {
	font-size: var(--font-size-s);
	color: var(--fg);
	opacity: 0.4;
}
.fi-row {
	padding: 8px var(--view-header-v-padding);
	font-size: var(--font-size);
	background-color: var(--bg);
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	column-gap: 8px;
}
.fi-row-message {
	/* Composed message in force info row */
	display: flex;
	column-gap: 8px;
	flex-wrap: wrap;
	align-items: baseline;
}
.fi-rowIcon {
	width: 22px;
}
.fi-rowIcon > .fi,
.fi-rowIcon > a > .fi {
	width:22px;
}
.fi-value {
	font-size: var(--font-size);
	opacity: 0.8;
	flex-basis: 100%;
}




.fi-counter {
	margin-left: 16px;
	margin-right: 16px;
}

.fi-pager-icon {
	cursor: pointer;
	user-select: none;
	-webkit-user-select: none;
}
.fi-pager-icon.fi-pager-disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

/* apply transition to moving elements */
.hslide-move,
.hslide-enter-active,
.hslide-leave-active {
	transition: all 300ms ease;
}

.hslide-enter-from {
	opacity: 0;
	transform: translateX(64px);
}
.hslide-leave-to {
	opacity: 0;
	transform: translateX(64px);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.hslide-leave-active {
	position: absolute;
}

</style>